import { motion } from 'framer-motion';
import { UserCircle2, Building2, ShieldCheck } from 'lucide-react';
import { SceneFrame, Pill } from './_atoms';

const assignments = [
  { entity: 'Rashid Hospital — AUH',  role: 'Admin',          cat: 'Provider', tone: 'teal' },
  { entity: 'Rashid Hospital — DXB',  role: 'Billing Clerk',  cat: 'Provider', tone: 'teal' },
  { entity: 'Aster Pharmacy — SHJ',   role: 'Auditor',        cat: 'Provider', tone: 'teal' },
];

export default function ArchAssignmentScene() {
  return (
    <SceneFrame
      eyebrow="User assignment model"
      title="One identity · many contexts"
      subtitle="A user holds a single identity across Eclaims. They may hold different roles across multiple entities — so long as every entity shares the same category."
    >
      <div className="grid grid-cols-[220px_60px_1fr] gap-0 items-start mt-4">

        {/* User */}
        <motion.div
          initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
          className="rounded-2xl bg-gradient-to-br from-teal-500 to-teal-700 text-white p-6 shadow-lg"
        >
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mb-3">
            <UserCircle2 size={26} className="text-white" />
          </div>
          <p className="font-body font-bold text-[14px]">Ahmed Shareef</p>
          <p className="font-body text-[11.5px] text-white/80 mt-0.5">asamoe@yourhealth.com</p>
          <div className="mt-3 pt-3 border-t border-white/20">
            <p className="text-[10px] font-body uppercase tracking-wider text-white/70">Single identity</p>
            <p className="text-[11.5px] font-body text-white/95">Organisation: Rashid Group</p>
          </div>
        </motion.div>

        {/* Connector */}
        <div className="flex flex-col items-center justify-center h-full py-10 gap-2">
          {assignments.map((_, i) => (
            <motion.svg
              key={i}
              initial={{ opacity: 0, pathLength: 0 }}
              animate={{ opacity: 1, pathLength: 1 }}
              transition={{ delay: 0.6 + i * 0.25, duration: 0.5 }}
              width="60" height="24" viewBox="0 0 60 24"
            >
              <motion.path
                d="M0 12 L55 12"
                stroke="#0D9488"
                strokeWidth="2"
                strokeDasharray="4 4"
                fill="none"
              />
              <circle cx="55" cy="12" r="4" fill="#0D9488" />
            </motion.svg>
          ))}
        </div>

        {/* Entities with roles */}
        <div className="space-y-3">
          {assignments.map((a, i) => (
            <motion.div
              key={a.entity}
              initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.55 + i * 0.25, duration: 0.45 }}
              className="rounded-xl bg-white border border-[#E5E7EB] px-5 py-4 flex items-center gap-4 shadow-sm"
            >
              <div className="w-10 h-10 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center flex-shrink-0">
                <Building2 size={18} className="text-teal-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-[13.5px] text-[#0A0A0A]">{a.entity}</p>
                <p className="text-[11px] font-body text-[#6B7280]">Category: {a.cat}</p>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck size={14} className="text-teal-600" />
                <span className="font-body text-[12.5px] font-bold text-teal-800 bg-teal-100 border border-teal-200 rounded-md px-2.5 py-1">{a.role}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.8 }}
        className="mt-6 grid grid-cols-3 gap-3"
      >
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3">
          <Pill tone="emerald">Rule 1</Pill>
          <p className="text-[11.5px] font-body text-emerald-900 mt-1.5">Single identity across the platform</p>
        </div>
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3">
          <Pill tone="emerald">Rule 2</Pill>
          <p className="text-[11.5px] font-body text-emerald-900 mt-1.5">Exactly one role per entity</p>
        </div>
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3">
          <Pill tone="emerald">Rule 3</Pill>
          <p className="text-[11.5px] font-body text-emerald-900 mt-1.5">All linked entities must share the same category</p>
        </div>
      </motion.div>
    </SceneFrame>
  );
}
